import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  /** Currently selected values. Treated as "all" when length === options.length. */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Label shown in the trigger + "all" row when every option is selected (e.g. "All counties"). */
  allLabel?: string;
  /** Word used for the "N selected" summary (e.g. "selected"). */
  selectedWord?: string;
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * Show a filter box above the list. Worth it past ~15 options — a bare
   * checkbox list of 200 subjects is harder to search than the native <select>
   * it replaces. Filtering is client-side over `options`; it never hides an
   * already-selected value, so nothing silently disappears while narrowing.
   */
  searchable?: boolean;
  /** Placeholder for the filter box (e.g. "Search subjects…"). */
  searchPlaceholder?: string;
  /** Pill-sized trigger for inline table cells (saved-view editors). Panel is unchanged. */
  compact?: boolean;
}

/**
 * Generic multi-select dropdown: a summary trigger + checkbox-row panel.
 * Enforces a non-empty selection — deselecting the last item reverts to "all selected".
 * Clicking the "All" row selects every option.
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selected,
  onChange,
  allLabel = 'All',
  selectedWord = 'selected',
  className = '',
  disabled = false,
  ariaLabel,
  searchable = false,
  searchPlaceholder = 'Search…',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  /*
    Open upward when there is no room below. Without this, a dropdown in the
    bottom row of a modal spills past the card's edge onto the page behind it,
    which reads as a rendering glitch. 300px ≈ the panel's 18rem max-height
    plus its 6px offset.
  */
  const [openUp, setOpenUp] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const PANEL_MAX_PX = 300;
  const toggleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const roomBelow = window.innerHeight - rect.bottom;
      setOpenUp(roomBelow < PANEL_MAX_PX && rect.top > roomBelow);
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    /*
      CAPTURE phase, not bubble. The shared Modal's panel calls
      stopPropagation() on mousedown (so panel clicks don't dismiss the
      dialog), which kills bubble-phase document listeners — inside any modal
      this dropdown could never close on an outside click. Capture runs
      document-first, before the panel's handler can stop the event.
    */
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [open]);

  // Reopening starts from the full list rather than the last search.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const allValues = options.map((o) => o.value);
  const isAll = selected.length === 0 || selected.length === options.length;
  const selectedSet = new Set(selected);

  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Selected options stay visible so narrowing never hides a current choice.
    const chosen = new Set(selected);
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || chosen.has(o.value)
    );
  }, [options, query, selected]);

  const summary = (() => {
    if (isAll) return allLabel;
    if (selected.length <= 2) {
      return options
        .filter((o) => selectedSet.has(o.value))
        .map((o) => o.label)
        .join(', ');
    }
    return `${selected.length} ${selectedWord}`;
  })();

  const toggleValue = (value: string) => {
    // From "all" (empty/full) state, first click narrows to just this one.
    if (isAll) {
      onChange([value]);
      return;
    }
    let next: string[];
    if (selectedSet.has(value)) {
      next = selected.filter((v) => v !== value);
      if (next.length === 0) next = allValues.slice(); // never empty → revert to all
    } else {
      next = [...selected, value];
    }
    // Keep option order; collapse to "all" sentinel ([]) when everything is selected.
    const ordered = allValues.filter((v) => next.includes(v));
    onChange(ordered.length === allValues.length ? [] : ordered);
  };

  const selectAll = () => onChange([]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        // Border width/style are set inline because a global, UNLAYERED `button { border: 0 }`
        // reset (Ionic normalize) beats Tailwind's layered `border` utilities regardless of
        // specificity — so class-based borders silently do nothing on buttons. Inline styles
        // outrank both. Colour stays class-driven (border-border-light-secondary / dark) so it
        // matches the native <select> inputs and still responds to dark mode.
        style={{ borderWidth: '1px', borderStyle: 'solid' }}
        className={
          compact
            ? 'flex items-center justify-between gap-1.5 rounded-full bg-background-tertiary dark:bg-background-dark-tertiary px-2.5 py-0.5 text-[11px] font-semibold text-text-secondary dark:text-text-dark-secondary border border-border-light-secondary dark:border-border-dark disabled:cursor-not-allowed disabled:opacity-60 max-w-full'
            : 'input flex w-full items-center justify-between gap-2 text-left border border-border-light-secondary dark:border-border-dark disabled:cursor-not-allowed disabled:opacity-60'
        }
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-text-tertiary dark:text-text-dark-tertiary" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          /* Force height/scroll with `!important` priority via a callback ref. Plain inline
             styles (and Tailwind `max-h-72`/`overflow-auto`) get overridden by mobile rules in
             index.css that set `max-height:none!important; overflow:visible!important` on
             elements inside `.card` under 1024px — only an inline `!important` declaration
             beats an author `!important` rule, and JS setProperty is the only way to set one. */
          ref={(el) => {
            if (!el) return;
            el.style.setProperty('max-height', '18rem', 'important');
            el.style.setProperty('overflow-y', 'auto', 'important');
            el.style.setProperty('overflow-x', 'hidden', 'important');
            el.style.setProperty('overscroll-behavior', 'contain', 'important');
          }}
          className={`absolute left-0 ${
            openUp ? 'bottom-[calc(100%+6px)]' : 'top-[calc(100%+6px)]'
          } z-30 min-w-full rounded-lg border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary p-1.5 shadow-lg`}
        >
          {searchable && (
            <div className="relative mb-1.5">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                autoFocus
                className="input w-full pl-8"
                // Typing must not toggle the row underneath or close the panel.
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {allLabel && !query.trim() && (
            <button
              type="button"
              role="option"
              aria-selected={isAll}
              onClick={selectAll}
              className="flex w-full items-center gap-2.5 whitespace-nowrap rounded-md px-2 py-2 text-left text-sm text-text-secondary dark:text-text-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary"
            >
              <span
                className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                  isAll
                    ? 'border-primary-700 bg-primary-700 text-white'
                    : 'border-border-light dark:border-border-dark'
                }`}
              >
                {isAll && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              {allLabel}
            </button>
          )}
          {searchable && visibleOptions.length === 0 && (
            <div className="px-2 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
              No matches
            </div>
          )}
          {visibleOptions.map((opt) => {
            const checked = !isAll && selectedSet.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={checked}
                onClick={() => toggleValue(opt.value)}
                className="flex w-full items-center gap-2.5 whitespace-nowrap rounded-md px-2 py-2 text-left text-sm text-text-secondary dark:text-text-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary"
              >
                <span
                  className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                    checked
                      ? 'border-primary-700 bg-primary-700 text-white'
                      : 'border-border-light dark:border-border-dark'
                  }`}
                >
                  {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
